import { useState, useEffect, useRef, useCallback } from "react";
import { GameProps } from "shared/games/types/GameConfiguration";
import { GameAnswerFrequencyFinder } from "shared/games/types/GameAnswers";
import { GameQuestionFrequencyFinder } from "shared/games/types/GameQuestions";
import * as GameLayout from "ui/templates/Game/LayoutComponents";
import Button from "ui/atoms/Button/Button";
import Switch, { SwitchItem } from "ui/atoms/Switch/Switch";
import Graph from "../organisms/Graph/Graph";
import { useMountEffect, useUnmountEffect } from "@react-hookz/web";
import { SingleAudioPlayer } from "shared/AudioPlayers/SingleAudioPlayer";
import { storagePath } from "apiRoutes/storagePath";
import { EQPlaybackControls } from "shared/AudioPlayers/types";
import getBandwidth from "util/eqMathHelpers/getBandwidth";
import getQFromBandwidthOctaves from "util/eqMathHelpers/getQFromBandwidthOctaves";
import { PlaybackState } from "../types";
import { difficultyConfig, freqRange } from "../constants";
import {
  gameTypeToBiquadFilterType,
  gameTypeToEQType,
} from "./gameTypeConvertors";
import GameTitle from "../molecules/GameTitle";
import MobileFloatingAutoToggleButton from "ui/templates/Game/molecules/MobileFloatingAutoToggleButton";
import useAutoToggle from "ui/templates/Game/hooks/useAutoToggle";
import { refrenceTrackIds, referenceTrackSteps } from "./referenceTrackConfig";
import ReferenceTrack from "ui/organisms/ReferenceTrack/ReferenceTrack";

type Props = GameProps<GameQuestionFrequencyFinder, GameAnswerFrequencyFinder>;

export default function FrequencyFinderGame({
  question,
  onLoaded,
  onSubmit,
  controlledPlayback,
  mobileToolbarStyle,
  submitInProgress,
  referenceTrackDisabled,
}: Props) {
  const [mixFrequency, setMixFrequency] = useState<number>(440);
  const audioPlayer = useRef<SingleAudioPlayer>();
  const { q, safeAreaInOctaves, gain } = difficultyConfig[question.difficulty];
  const {
    playbackState,
    setPlaybackState,
    autoToggle,
    setAutoToggle,
    secondsTillChange,
  } = useAutoToggle<PlaybackState>({
    defaultPlaybackState: "mix",
    looop: question.looop,
    getNextPlaybackState: (prev) => (prev === "mix" ? "reference" : "mix"),
    enabled: controlledPlayback !== "pause",
  });

  useMountEffect(() => {
    const player = new SingleAudioPlayer(
      storagePath(
        `${question.looop.base_audio_url}${question.instrument.audio_files.default}`
      ),
      question.looop
    );

    player.load().then(() => {
      if (controlledPlayback !== "pause") {
        player.start();
      }

      onLoaded();
    });

    audioPlayer.current = player;
  });

  useEffect(() => {
    if (controlledPlayback === "play") {
      audioPlayer.current?.start();
    } else if (controlledPlayback === "pause") {
      audioPlayer.current?.stop();
    }
  }, [controlledPlayback]);

  useUnmountEffect(() => {
    audioPlayer.current?.dispose();
    audioPlayer.current = undefined;
  });

  useEffect(() => {
    const frequency =
      playbackState === "mix" ? mixFrequency : question.frequency;

    const eq: EQPlaybackControls = {
      [frequency]: {
        frequency,
        gain: gain(question.type),
        q,
        type: gameTypeToBiquadFilterType(question.type),
      },
    };

    audioPlayer.current?.setPlaybackStatus({ eq });
  }, [gain, mixFrequency, playbackState, q, question.frequency, question.type]);

  const handleSubmit = useCallback(() => {
    const { lowerFreq, upperFreq } = getBandwidth(
      question.frequency,
      getQFromBandwidthOctaves(safeAreaInOctaves)
    );

    const correct = mixFrequency >= lowerFreq && mixFrequency <= upperFreq;

    onSubmit({
      type: question.type,
      correct,
      correctFrequency: question.frequency,
      submittedFrequency: mixFrequency,
    });
  }, [
    mixFrequency,
    onSubmit,
    question.frequency,
    question.type,
    safeAreaInOctaves,
  ]);

  return (
    <>
      <GameLayout.Toolbar mobileStyle={mobileToolbarStyle}>
        <div className={"flex justify-between"}>
          <div className={"flex items-center"}>
            <div className={"mr-2"} id={refrenceTrackIds.playbackState}>
              <Switch
                size="large"
                items={items}
                value={playbackState}
                onChange={(value) => {
                  setAutoToggle(false);
                  setPlaybackState(value);
                }}
                wholeButtonToggle
              />
            </div>

            <div className={"hidden md:block"}>
              <Button
                theme={"default"}
                size="large"
                outline
                active={autoToggle}
                onClick={() => setAutoToggle((x) => !x)}
              >
                Auto Toggle {secondsTillChange}
              </Button>
            </div>
          </div>

          <Button
            id={refrenceTrackIds.submit}
            theme="green"
            size="large"
            onClick={handleSubmit}
            loading={submitInProgress}
          >
            Submit
          </Button>
        </div>
      </GameLayout.Toolbar>

      <MobileFloatingAutoToggleButton
        mobileToolbarStyle={mobileToolbarStyle}
        autoToggle={autoToggle}
        setAutoToggle={setAutoToggle}
        secondsTillChange={secondsTillChange}
      />

      <GameLayout.Container>
        <Graph
          measurementKey={"frequency-finder"}
          type={gameTypeToEQType(question.type)}
          text={<GameTitle playbackState={playbackState} />}
          freqRange={freqRange}
          freq={mixFrequency}
          onChange={setMixFrequency}
          q={q}
          safeAreaInOctaves={safeAreaInOctaves}
        />
      </GameLayout.Container>

      <ReferenceTrack
        localStorageKey="freq-finder"
        getSteps={referenceTrackSteps}
        disabled={referenceTrackDisabled}
      />
    </>
  );
}

const items: SwitchItem<PlaybackState>[] = [
  { label: { mobile: "Mix", desktop: "Mix" }, value: "mix" },
  {
    label: { mobile: "Ref", desktop: "Reference" },
    value: "reference",
  },
];
